import localforage from 'localforage';
import cloneDeep from 'lodash.clonedeep';
import VuexPersist from 'vuex-persist';
import { decodeJwt } from '@/utilities';
import { getDifferenceInMinutes } from '@/utilities/DateUtils';
import store from '@/store';
import { resolveStorageReadyHandler } from '@/utilities/resolveStorageReadyHandler';
import IdentityClient from '../../api/IdentityClient';
import User from '../../models/User';

const filteredMutations = [
  'setUser',
  'setUserSettings',
  'setCarfaxTokens',
  'setUserAppliedSavedSearchIds',
];

const vuexUserStorage = new VuexPersist({
  key: 'acv-user-storage',
  storage: localforage,
  asyncStorage: true,
  // Do not save everything by default - ETC
  modules: [
    'authorization',
  ],
  filter: (mutation) => filteredMutations.includes(mutation.type),
  restoreState: (key, indexeddb) => new Promise((resolve) => {
    indexeddb
      .getItem(key)
      .then(async (storage) => {
        // If we have a saved state, use that.
        // Otherwise, get the default values from the store - ETC
        const authorizationStorage = storage ? cloneDeep(storage) : store.state;

        const {
          authorization: {
            user: indexeddbUser,
          },
        } = authorizationStorage;

        try {
          let jwtToken = '';
          let refreshToken = '';
          if (window.localStorage) {
            const tokenStorage = window
              .localStorage
              .getItem('acv-jwt-tokens');
            try {
              jwtToken = JSON.parse(tokenStorage).jwtToken;
              refreshToken = JSON.parse(tokenStorage).refreshToken;
            } catch (err) {
              // do nothing
            }
          }

          const decodedJwtToken = decodeJwt(jwtToken) ?? '';
          const jwtExpirationVal = new Date(0);
          jwtExpirationVal.setUTCSeconds(decodedJwtToken?.exp ?? 0);
          const currentDate = new Date();
          const jwtDifferenceInMinutes = getDifferenceInMinutes(
            jwtExpirationVal, // start date
            currentDate, // end date
          );
          const tokensCloseToExpiration = jwtDifferenceInMinutes < 3;
          if (tokensCloseToExpiration) {
            const identityClient = new IdentityClient(indexeddbUser);
            let refreshResponse;

            if (refreshToken !== '' && !window.acvNative) {
              refreshResponse = await identityClient.refreshToken({
                jwtToken,
                refreshToken,
              });
            }
            const newTokens = refreshResponse.data || {};
            window.localStorage.setItem('acv-jwt-tokens', JSON.stringify({
              ...newTokens,
              lastUpdated: new Date(),
            }));
          }
        } catch (err) {
          // do nothing
        } finally {
          authorizationStorage.authorization.user = new User(indexeddbUser);
          // the user initialization used to happen in auth module
          // in migrateJwtTokensToStorage method. although the method
          // is no longer necessary, we still need to initialize
          // the user to get session tokens and have a userId in state
          const {
            authorization: {
              jwtTokenExpirationDateTime,
            },
          } = authorizationStorage;
          if (jwtTokenExpirationDateTime) {
            authorizationStorage
              .authorization
              .jwtTokenExpirationDateTime = new Date(jwtTokenExpirationDateTime);
          }
          resolve(authorizationStorage);
          resolveStorageReadyHandler(); // Trigger the promise resolve so storage is ready in router
        }
      });
  }),
  saveState: (key, state, storage) => {
    delete state.authorization.refreshTokenRequest;
    storage.setItem(key, state);
  },
});

export default vuexUserStorage;
